import { useEffect, useState } from "react";
import AdminHeader from "@/pages/admin/components/AdminHeader";
import ConfirmDialog from "@/pages/admin/components/ConfirmDialog";
import HeroSlideModal from "@/pages/admin/components/HeroSlideModal";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { buildHeroDestination, useHeroSlides, type HeroSlide } from "@/hooks/useHeroSlides";

export default function AdminHero() {
  const { slides, addSlide, updateSlide, deleteSlide, routeOptions, marketplaceOptions } = useHeroSlides();
  const { settings, updateSettings } = useSiteSettings();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<HeroSlide | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [heroTitleDraft, setHeroTitleDraft] = useState(settings.heroTitle);
  const [heroSubtitleDraft, setHeroSubtitleDraft] = useState(settings.heroSubtitle);

  useEffect(() => {
    setHeroTitleDraft(settings.heroTitle);
    setHeroSubtitleDraft(settings.heroSubtitle);
  }, [settings.heroSubtitle, settings.heroTitle]);

  const heroTextDirty = heroTitleDraft !== settings.heroTitle || heroSubtitleDraft !== settings.heroSubtitle;

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = (data: Omit<HeroSlide, "id">) => {
    if (editingSlide) {
      updateSlide(editingSlide.id, data);
      showToast("Slide do Hero atualizado com sucesso!");
    } else {
      addSlide(data);
      showToast("Slide do Hero adicionado com sucesso!");
    }

    setModalOpen(false);
    setEditingSlide(null);
  };

  const handleSaveHeroText = () => {
    updateSettings({
      heroTitle: heroTitleDraft.trim(),
      heroSubtitle: heroSubtitleDraft.trim(),
    });
    showToast("Texto da Hero atualizado com sucesso!");
  };

  const handleDelete = () => {
    if (deletingId === null) return;
    deleteSlide(deletingId);
    setDeletingId(null);
    showToast("Slide removido do Hero.");
  };

  return (
    <div>
      <AdminHeader
        title="Hero"
        subtitle="Gerencie os banners do carrossel principal da página inicial"
        action={
          <button
            onClick={() => {
              setEditingSlide(null);
              setModalOpen(true);
            }}
            className="px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1.5"></i>Novo Slide
          </button>
        }
      />

      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-7">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 text-orange-500">
            <i className="ri-font-size-2 text-base"></i>
          </div>
          <h2 className="text-base font-bold text-gray-900">Texto da Hero</h2>
        </div>
        <p className="text-sm text-gray-500 mb-5">
          Edite o título e o subtítulo principais exibidos sobre o banner da Hero.
        </p>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Título principal</label>
            <textarea
              value={heroTitleDraft}
              onChange={(e) => setHeroTitleDraft(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Subtítulo</label>
            <textarea
              value={heroSubtitleDraft}
              onChange={(e) => setHeroSubtitleDraft(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
            />
          </div>

          <div className="rounded-2xl border border-gray-100 overflow-hidden bg-gray-950">
            <div className="px-5 py-3 border-b border-white/10 text-xs font-semibold uppercase tracking-wide text-white/50">
              Preview do texto
            </div>
            <div className="p-6 bg-gradient-to-r from-black/85 via-black/70 to-black/50">
              <h3 className="text-2xl font-black text-white leading-tight whitespace-pre-line">
                {heroTitleDraft}
              </h3>
              <p className="text-white/75 text-sm mt-3 max-w-2xl whitespace-pre-line">
                {heroSubtitleDraft}
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSaveHeroText}
              disabled={!heroTextDirty}
              className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-colors whitespace-nowrap ${
                heroTextDirty
                  ? "bg-orange-500 text-white hover:bg-orange-600 cursor-pointer"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              <i className="ri-save-line mr-1.5"></i>Salvar Texto da Hero
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-5 mb-7">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 text-orange-600 mb-3">
            <i className="ri-slideshow-line text-lg"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{slides.length}</p>
          <p className="text-sm font-medium text-gray-700 mt-0.5">Slides ativos</p>
          <p className="text-xs text-gray-400 mt-0.5">Exibidos no carrossel da home</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-sky-50 text-sky-600 mb-3">
            <i className="ri-links-line text-lg"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{slides.filter((slide) => slide.marketplace !== "Todos").length}</p>
          <p className="text-sm font-medium text-gray-700 mt-0.5">Com marketplace filtrado</p>
          <p className="text-xs text-gray-400 mt-0.5">Redirecionam para uma loja específica</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 mb-3">
            <i className="ri-eye-line text-lg"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">Home</p>
          <p className="text-sm font-medium text-gray-700 mt-0.5">Destino da edição</p>
          <p className="text-xs text-gray-400 mt-0.5">Os slides aparecem no topo da página inicial</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Preview</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Categoria</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Marketplace</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Destino</th>
              <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ações</th>
            </tr>
          </thead>
          <tbody>
            {slides.map((slide) => (
              <tr key={slide.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                <td className="px-5 py-4">
                  <div className="w-40 h-20 rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                    <img src={slide.imageUrl} alt={slide.categoryLabel} className="w-full h-full object-cover object-center" />
                  </div>
                </td>
                <td className="px-5 py-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{slide.categoryLabel}</p>
                    <p className="text-xs text-gray-400 mt-1">{slide.categoryPath}</p>
                  </div>
                </td>
                <td className="px-5 py-4">
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${slide.marketplace === "Todos" ? "bg-gray-100 text-gray-600" : "bg-orange-50 text-orange-600"}`}>
                    {slide.marketplace}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <a
                    href={buildHeroDestination(slide.categoryPath, slide.marketplace)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-orange-600 hover:underline break-all"
                  >
                    {buildHeroDestination(slide.categoryPath, slide.marketplace)}
                  </a>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => {
                        setEditingSlide(slide);
                        setModalOpen(true);
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-orange-50 text-gray-400 hover:text-orange-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setDeletingId(slide.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <HeroSlideModal
          slide={editingSlide}
          routeOptions={routeOptions}
          marketplaceOptions={marketplaceOptions}
          onSave={handleSave}
          onClose={() => {
            setModalOpen(false);
            setEditingSlide(null);
          }}
        />
      )}

      {deletingId !== null && (
        <ConfirmDialog
          title="Remover Slide"
          message="Tem certeza que deseja remover este slide do Hero?"
          onConfirm={handleDelete}
          onCancel={() => setDeletingId(null)}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-xl z-50">
          <i className="ri-checkbox-circle-line text-green-400 mr-2"></i>
          {toast}
        </div>
      )}
    </div>
  );
}
